import { useMemo } from 'react';
import { buildTerritorialScoring } from '../utils/buildTerritorialScoring';
import { buildTerritorialState } from '../utils/buildTerritorialState';
import { buildExecutiveReasoning } from '../utils/buildExecutiveReasoning';
import { buildRelationalEngine } from '../utils/buildRelationalEngine';
import { buildNarrativeConvergence } from '../intelligence/buildNarrativeConvergence';

export function useVitaEngine(actors = [], alerts = [], signals = []) {

  const processedActors = useMemo(() => {
    const convergence = buildNarrativeConvergence({
      signals,
      alerts,
      actors,
      timeline: []
    });
    return actors.map((actor) =>
      processActor(actor, alerts, signals, convergence.convergenciaPorActor)
    );
  }, [actors, alerts, signals]);

  const processedActorsWithRelations = useMemo(() => {
    return buildRelationalEngine(processedActors);
  }, [processedActors]);

  const getTerritorialSummary = useMemo(() => {
    return buildProjectSummary(processedActorsWithRelations, alerts, signals);
  }, [processedActorsWithRelations, alerts, signals]);

  const actoresPorEstado = useMemo(() => {
    return groupByState(processedActorsWithRelations);
  }, [processedActorsWithRelations]);

  const actoresCriticos = useMemo(() => {
    return processedActorsWithRelations
      .filter((a) => a._vita.scoring.IVT >= 55)
      .sort((a, b) => b._vita.scoring.IVT - a._vita.scoring.IVT);
  }, [processedActorsWithRelations]);

  const estadoTerritorialProyecto = useMemo(() => {
    return getDominantState(processedActorsWithRelations);
  }, [processedActorsWithRelations]);

  const actoresPuente = useMemo(() => {
    return processedActorsWithRelations
      .filter((a) => a._vita?.relacional?.esPuente)
      .sort((a, b) => b._vita.scoring.IVT - a._vita.scoring.IVT);
  }, [processedActorsWithRelations]);

  const actoresAislados = useMemo(() => {
    return processedActorsWithRelations.filter(
      (a) => (a._vita?.relacional?.grado || 0) === 0
    );
  }, [processedActorsWithRelations]);

  return {
    processedActors: processedActorsWithRelations,
    getTerritorialSummary,
    actoresPorEstado,
    actoresCriticos,
    estadoTerritorialProyecto,
    actoresPuente,
    actoresAislados
  };
}

function processActor(actor, alerts, signals, convergenciaPorActor = {}) {
  const enriched = enrichActorWithSignals(actor, alerts, signals);

  const convergenciaReal = convergenciaPorActor[actor.id] || null;
  if (convergenciaReal) {
    enriched.convergenciaNarrativa = convergenciaReal;
    enriched._convergenciaEsReal = true;
  }

  const scoring = buildTerritorialScoring(enriched);
  const state = buildTerritorialState(scoring, enriched);
  const reasoning = buildExecutiveReasoning({
    ...enriched,
    estadoTerritorial: enriched.estadoTerritorial || state.state,
    _calculatedState: state.state,
    _scoring: scoring
  });

  return {
    ...actor,
    _vita: {
      scoring,
      state,
      reasoning,
      enriched,
      relacional: null,
      processedAt: new Date().toISOString()
    }
  };
}

function enrichActorWithSignals(actor, alerts, signals) {
  const nombreActor = String(actor.nombre || '').toLowerCase().trim();

  const alertasVinculadas = alerts.filter((alerta) =>
    String(alerta.actor || '').toLowerCase().includes(nombreActor) ||
    String(alerta.tags || '').toLowerCase().includes(nombreActor)
  );

  const señalesVinculadas = signals.filter((signal) =>
    String(signal.actor || '').toLowerCase().includes(nombreActor) ||
    String(signal.tags || '').toLowerCase().includes(nombreActor)
  );

  const presionRRSS = calcularPresionRRSS(señalesVinculadas);

  const tieneAlertaCritica = alertasVinculadas.some(
    (a) => a.nivel === 'Crítico' || a.nivel === 'Alto'
  );

  return {
    ...actor,
    presionRRSS: actor.presionRRSS || presionRRSS,
    _alertasVinculadas: alertasVinculadas.length,
    _señalesVinculadas: señalesVinculadas.length,
    _tieneAlertaCritica: tieneAlertaCritica,
    activacionTerritorial:
      tieneAlertaCritica && !actor.activacionTerritorial
        ? 'Alta'
        : actor.activacionTerritorial
  };
}

function calcularPresionRRSS(señales) {
  if (!señales.length) return '';
  const criticas = señales.filter(
    (s) => s.riesgo === 'Crítico' || s.sentimiento === 'Negativo crítico'
  ).length;
  const altas = señales.filter(
    (s) => s.riesgo === 'Alto' || s.sentimiento === 'Negativo'
  ).length;
  if (criticas >= 2 || señales.length >= 5) return 'Crítica';
  if (criticas >= 1 || altas >= 2) return 'Alta';
  if (altas >= 1 || señales.length >= 2) return 'Media';
  return 'Baja';
}

function buildProjectSummary(processedActors, alerts, signals) {
  if (!processedActors.length) {
    return {
      estadoGeneral: 'Sin datos',
      narrativaDominante: 'Sin narrativa registrada',
      totalActores: 0,
      actoresCriticos: 0,
      IVTPromedio: 0,
      alertasActivas: 0,
      señalesActivas: 0,
      actoresPuente: 0,
      actoresAislados: 0,
      recomendacion: 'Registrar stakeholders para iniciar monitoreo territorial.'
    };
  }

  const IVTPromedio = Math.round(
    processedActors.reduce((sum, a) => sum + (a._vita?.scoring?.IVT || 0), 0) /
    processedActors.length
  );

  const actoresCriticos = processedActors.filter(
    (a) => (a._vita?.scoring?.IVT || 0) >= 55
  ).length;

  const alertasActivas = alerts.filter((a) => a.estado !== 'Cerrada').length;

  const actoresPuente = processedActors.filter(
    (a) => a._vita?.relacional?.esPuente
  ).length;

  const actoresAislados = processedActors.filter(
    (a) => (a._vita?.relacional?.grado || 0) === 0
  ).length;

  const narrativas = processedActors
    .map((a) => a._vita?.reasoning?.narrativaDominante)
    .filter(Boolean);

  const narrativaDominante = getMostFrequent(narrativas) || 'En análisis';
  const estadoGeneral = getDominantState(processedActors);

  return {
    estadoGeneral,
    narrativaDominante,
    totalActores: processedActors.length,
    actoresCriticos,
    IVTPromedio,
    alertasActivas,
    señalesActivas: signals.length,
    actoresPuente,
    actoresAislados,
    recomendacion: buildRecomendacionGeneral(estadoGeneral, actoresCriticos)
  };
}

function buildRecomendacionGeneral(estado, criticos) {
  if (estado === 'Escalamiento Territorial')
    return 'Activar protocolo de gestión territorial prioritaria. Atención inmediata requerida.';
  if (estado === 'Convergencia Narrativa')
    return `${criticos} actor(es) en convergencia. Iniciar acercamiento preventivo territorial.`;
  if (estado === 'Activación Territorial')
    return 'Se detectan señales de movilización. Priorizar contacto con actores activos.';
  if (estado === 'Sensibilidad Creciente')
    return 'Territorio sensible. Mantener monitoreo intensivo y preparar comunicación preventiva.';
  return 'Territorio estable. Continuar monitoreo regular.';
}

const ESTADO_SEVERIDAD = {
  'Escalamiento Territorial': 7,
  'Convergencia Narrativa': 6,
  'Activación Territorial': 5,
  'Sensibilidad Creciente': 4,
  'Observación': 3,
  'Contención Territorial': 2,
  'Estabilización': 1,
  'Estable': 0
};

function getDominantState(processedActors) {
  if (!processedActors.length) return 'Estable';
  let dominante = 'Estable';
  let maxSeveridad = -1;
  for (const actor of processedActors) {
    const estado = actor._vita?.state?.state || 'Estable';
    const severidad = ESTADO_SEVERIDAD[estado] ?? 0;
    if (severidad > maxSeveridad) {
      maxSeveridad = severidad;
      dominante = estado;
    }
  }
  return dominante;
}

function groupByState(processedActors) {
  const groups = {};
  for (const actor of processedActors) {
    const state = actor._vita?.state?.state || 'Estable';
    if (!groups[state]) groups[state] = [];
    groups[state].push(actor);
  }
  return groups;
}

function getMostFrequent(arr) {
  if (!arr.length) return null;
  const freq = {};
  for (const item of arr) freq[item] = (freq[item] || 0) + 1;
  return Object.entries(freq).sort((a, b) => b[1] - a[1])[0][0];
}